import { useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  Card,
  Checkbox,
  Col,
  Form,
  FormInstance,
  Info,
  Input,
  Radio,
  Select,
  Text,
  Title,
} from 'components/common'
import {
  dateRegexFormat,
  daysInAWeek,
  weeksInAMonths,
} from 'lib/constants/bookings'

export interface SearchConditionsCardProps {
  title?: string
  form?: FormInstance
}

export const SearchConditionsCard = ({
  title,
  form,
  ...reset
}: SearchConditionsCardProps): JSX.Element => {
  const [value, setValue] = useState(1)
  const [groupOfWeekDaysIndeterminate, setGroupOfWeekDaysIndeterminate] =
    useState(false)
  const [groupOfWeekDaysCheckAll, setGroupOfWeekDaysCheckAll] = useState(false)
  const [indeterminate, setIndeterminate] = useState(false)
  const [checkAll, setCheckAll] = useState(false)

  const onChange = (e) => {
    setValue(e.target.value)
  }

  const onChangeCheckBox = (list) => {
    setGroupOfWeekDaysIndeterminate(
      !!list.length && list.length < daysInAWeek.length
    )
    setGroupOfWeekDaysCheckAll(list.length === daysInAWeek.length)
  }

  const onCheckAllChange = (e) => {
    if (e.target.checked) {
      form.setFieldsValue({ groupOfWeekDays: daysInAWeek })
    } else {
      form.setFieldsValue({ groupOfWeekDays: [] })
    }
    setGroupOfWeekDaysIndeterminate(false)
    setGroupOfWeekDaysCheckAll(e.target.checked)
  }

  const onChangeCheckBoxGroupOfDays = (list) => {
    setIndeterminate(!!list.length && list.length < daysInAWeek.length)
    setCheckAll(list.length === daysInAWeek.length)
  }

  const onCheckAllChangeGroupOfDays = (e) => {
    if (e.target.checked) {
      form.setFieldsValue({ groupOfDays: daysInAWeek })
    } else {
      form.setFieldsValue({ groupOfDays: [] })
    }
    setIndeterminate(false)
    setCheckAll(e.target.checked)
  }

  const weekendMemo = useMemo(() => {
    return weeksInAMonths.map((value, index) => (
      <Select.Option key={`${value}${index}`} value={index + 1}>
        {value}
      </Select.Option>
    ))
  }, [])

  const daysInAMonth = useMemo(() => {
    const days = []
    for (let i = 1; i <= 31; i++) {
      const x = i < 10 ? `0${i}` : i
      days.push(x)
    }
    return days.map((value, index) => (
      <Select.Option key={`${value}${index}`} value={value}>
        {value}
      </Select.Option>
    ))
  }, [])

  return (
    <>
      <Card style={{ marginBottom: '10px', marginRight: '5px' }}>
        <Title level={5}>{title}</Title>
        <Form.Item name="groupOfCondition" initialValue={1}>
          <Radio.Group onChange={onChange} value={value} {...reset}>
            <Col span={24} className={clsx('mb-5')}>
              <Radio value={1}>
                <Col span={24} style={{ marginBottom: '5px' }}>
                  <Info title="選択された日付の中から毎月選択された日付のみを絞り込みます。1月1日から3月30日が選択され、日次で「03」が選択されている場合、1月3日、2月3日、3月3日が絞り込まれます。">
                    日次
                  </Info>
                </Col>
                <span
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  <Form.Item name="selectedDay" {...reset}>
                    <Select style={{ width: 100 }} disabled={value !== 1}>
                      {daysInAMonth}
                    </Select>
                  </Form.Item>
                </span>
              </Radio>
            </Col>
            <Col span={24} className={clsx('mb-5')}>
              <Radio value={2}>
                <Col span={24} style={{ marginBottom: '5px' }}>
                  <Info title="選択されている日付のうち、特定の週の曜日のみを絞り込みます。日付が1月1日から3月30日が選択され、週次で「03」と「月」選択されている場合、1月第3週の月曜日、2月第3週の月曜日、3月第3三州の月曜日が絞り込まれます。">
                    週次
                  </Info>
                </Col>
                <Col span={24} className={clsx('mb-5')}>
                  <span
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                  >
                    <Form.Item
                      name="weekNumberInMonth"
                      style={{ marginBottom: '0px' }}
                      rules={[
                        { required: value === 2, message: '必須項目です' },
                      ]}
                      {...reset}
                    >
                      <Select style={{ width: 100 }} disabled={value !== 2}>
                        {weekendMemo}
                      </Select>
                    </Form.Item>
                  </span>
                </Col>
                <Col span={24}>
                  <Checkbox
                    indeterminate={groupOfWeekDaysIndeterminate}
                    onChange={onCheckAllChange}
                    checked={groupOfWeekDaysCheckAll}
                    disabled={value !== 2}
                  >
                    すべて
                  </Checkbox>
                  <Form.Item
                    name="groupOfWeekDays"
                    initialValue={''}
                    rules={[{ required: value === 2, message: '必須項目です' }]}
                    {...reset}
                  >
                    <Checkbox.Group
                      options={daysInAWeek}
                      onChange={onChangeCheckBox}
                      disabled={value !== 2}
                    />
                  </Form.Item>
                </Col>
              </Radio>
            </Col>
            <Col span={24} className={clsx('mb-5')}>
              <Radio value={3}>
                <Col span={24} style={{ marginBottom: '5px' }}>
                  <Info title="選択されている日付の間の該当する曜日のみが絞り込稀ます。">
                    曜日
                  </Info>
                </Col>
                <Col span={24}>
                  <Checkbox
                    indeterminate={indeterminate}
                    onChange={onCheckAllChangeGroupOfDays}
                    checked={checkAll}
                    disabled={value !== 3}
                  >
                    すべて
                  </Checkbox>
                </Col>
                <Form.Item
                  name="groupOfDays"
                  initialValue={''}
                  rules={[{ required: value === 3, message: '必須項目です' }]}
                  {...reset}
                >
                  <Checkbox.Group
                    options={daysInAWeek}
                    onChange={onChangeCheckBoxGroupOfDays}
                    disabled={value !== 3}
                  />
                </Form.Item>
              </Radio>
            </Col>
            <Col span={24} className={clsx('mb-5')}>
              <Radio value={4}>
                <Col span={24} style={{ marginBottom: '5px' }}>
                  <Info title="選択された日付に含まれる特定の日付をカンマ区切りで入力してください。">
                    任意
                  </Info>
                </Col>
                <Form.Item
                  name="customDates"
                  style={{ marginBottom: '0px' }}
                  rules={[
                    {
                      pattern: dateRegexFormat,
                      message: '有効な日付を教えてください。',
                    },
                  ]}
                  extra={
                    <Text type="secondary">
                      日付をカンマ（,）区切りで入力してください。
                    </Text>
                  }
                  {...reset}
                >
                  <Input
                    autoComplete="off"
                    disabled={value !== 4}
                    placeholder="2022-04-01,2022-04-02,2022-04-03"
                  />
                </Form.Item>
              </Radio>
            </Col>
          </Radio.Group>
        </Form.Item>
      </Card>
    </>
  )
}
